// Core imports
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { Row, Col, Divider } from 'antd';

// Misc imports
import useContainerResize from '../../utils/hooks/useContainerResize';

const LeftPanel = ({ children }) => <>{children}</>;
LeftPanel.propTypes = { children: PropTypes.node };
LeftPanel.displayName = 'ResizablePanelsWrapper.LeftPanel';

const RightPanel = ({ children }) => <>{children}</>;
RightPanel.propTypes = { children: PropTypes.node };
RightPanel.displayName = 'ResizablePanelsWrapper.RightPanel';

const ResizablePanelsWrapper = ({
  children,
  initialWidth = 340,
  minWidth = 150,
  maxWidth = '75%',
  storageKey = null,
  dragLeft = false,
  isMobileOrTabletSize,
  isResizingDisabled = true,
  resetValueTrigger,
  minHeight='auto'
}) => {
  const firstPanelRef = useRef(null);
  const currentResetValueTrigger = useRef(null);
  const { width: firstPanelWidth, startResizing, setWidth } = useContainerResize(
    firstPanelRef,
    storageKey,
    initialWidth,
    dragLeft,
    minWidth,
    maxWidth
  );

  useEffect(() => {
    if (!resetValueTrigger) {
      return;
    }
    if (resetValueTrigger !== currentResetValueTrigger.current) {
      setWidth(initialWidth);
      currentResetValueTrigger.current = resetValueTrigger;
    }
  }, [resetValueTrigger, initialWidth, setWidth]);

  const showDraggableResizer = !isMobileOrTabletSize;

  let firstPanelContent = null;
  let secondPanelContent = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === LeftPanel) {
        firstPanelContent = child.props.children;
      } else if (child.type === RightPanel) {
        secondPanelContent = child.props.children;
      }
    }
  });

  return (
    <Row
      wrap={!!isMobileOrTabletSize}
    >
      <Col
        ref={firstPanelRef}
        className="resizable-panel-wrapper-panel"
        style={{
          width: isMobileOrTabletSize ? '100%' : `${firstPanelWidth}px`,
          minHeight: isMobileOrTabletSize ? 'auto' : minHeight
        }}
        flex={isMobileOrTabletSize ? '1 1 100%' : `0 0 ${firstPanelWidth}px`}
      >
        {firstPanelContent}
      </Col>

      {showDraggableResizer ? (
        <Col
          className="align-items-stretch d-flex user-select-none flex-shrink-0"
        >
          <Divider type="vertical" className="h-100 m-0" />
          {
            !isResizingDisabled && (
              <div
                className="resize-handle left-0 top-0"
                onMouseDown={startResizing}
              />
            )
          }
        </Col>
      ) : (
        isMobileOrTabletSize && (
          <Col className="d-flex align-items-stretch w-auto resizable-panel-wrapper-panel">
            <Divider className="w-100 my-1" />
          </Col>
        )
      )}

      <Col
        flex="auto"
        className="resizable-panel-wrapper-panel d-flex flex-column"
        style={{
          minHeight: isMobileOrTabletSize ? 'auto' : minHeight
        }}
      >
        {secondPanelContent}
      </Col>
    </Row>
  );
};

ResizablePanelsWrapper.LeftPanel = LeftPanel;
ResizablePanelsWrapper.RightPanel = RightPanel;

ResizablePanelsWrapper.propTypes = {
  children: (props, propName, componentName) => {
    const childrenArray = React.Children.toArray(props[propName]);
    if (childrenArray.length !== 2) {
      return new Error(
        `Invalid \`${propName}\` supplied to \`${componentName}\`. Expected exactly two children: \`<${componentName}.LeftPanel>\` and \`<${componentName}.RightPanel>\`. Received ${childrenArray.length} children.`
      );
    }
    const firstChild = childrenArray[0];
    const secondChild = childrenArray[1];
    if (!React.isValidElement(firstChild) || firstChild.type !== ResizablePanelsWrapper.LeftPanel) {
      return new Error(
        `Invalid first child supplied to \`${componentName}\`. Expected a \`<${componentName}.LeftPanel>\` component. Received \`${firstChild.type.displayName || firstChild.type.name || typeof firstChild.type}\`.`
      );
    }
    if (!React.isValidElement(secondChild) || secondChild.type !== ResizablePanelsWrapper.RightPanel) {
      return new Error(
        `Invalid second child supplied to \`${componentName}\`. Expected a \`<${componentName}.RightPanel>\` component. Received \`${secondChild.type.displayName || secondChild.type.name || typeof secondChild.type}\`.`
      );
    }
    return null;
  },
  FirstPanelComponent: PropTypes.node.isRequired,
  SecondPanelComponent: PropTypes.node.isRequired,
  initialWidth: PropTypes.number,
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  storageKey: PropTypes.string,
  dragLeft: PropTypes.bool,
  isMobileOrTabletSize: PropTypes.bool,
  isResizingDisabled: PropTypes.bool,
  resetValueTrigger: PropTypes.any
};

export default ResizablePanelsWrapper;
